import React, { useEffect, useState } from 'react'
import '../common.css'
import AppDataGrid from '../../global/AppDataGrid'
import AppPageHeader from '../../global/AppPageHeader'
import RooftopPriceActions from '../priceActions/RooftopPriceActions'
import { Tooltip } from '@mui/material'
import Header from '../../global/GridFilterHeader'
import { ApiService } from '@carrier-ecat/workflowui-globalfunctions'

import { apiUrl } from '../../../environments/environments'
import Warning from '@mui/icons-material/Warning'
import useGridRowDeletion from '../../global/useGridRowDeletion'
import { hasActiveFilters } from '../utils/filters'
import { useIsProjectEditor } from '../../../hooks/useUserPermissions'

const REQUIRED_FIELDS = ['productType', 'size', 'assembly', 'varcode']

const getMissingFields = (row) => REQUIRED_FIELDS.filter((f) => !row[f])

const getRowId = (row) => `${row.productType}-${row.size}-${row.assembly}-${row.varcode}`

function QmPage() {
    const [data, setData] = useState([])
    const [dataToDisplay, setDataToDisplay] = useState([])
    const [loading, setLoading] = useState(true)
    const [filters, setFilters] = useState({})
    const [notice, setNotice] = useState(null)
    const isProjectEditor = useIsProjectEditor()

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }))
    }

    const refreshData = async () => {
        setLoading(true)
        try {
            const { status, data } = await ApiService(`${apiUrl}/rooftop/qm/`)
            if (status === 200 && data) {
                setData(data.result)
            }
        } catch (error) {
            console.error('Error loading QM prices:', error)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        refreshData()
    }, [])

    useEffect(() => {
        let filtered = [...data]

        Object.entries(filters).forEach(([field, value]) => {
            const raw = value == null ? '' : String(value).trim()
            if (raw === '') return

            filtered = filtered.filter((row) => {
                const fieldValue = row[field]

                if (field === 'price') {
                    if (fieldValue == null || fieldValue === '') return raw === '-' || raw === ''
                    return String(fieldValue).includes(raw)
                }

                if (fieldValue == null || fieldValue === '') return false
                return String(fieldValue).toLowerCase().includes(raw.toLowerCase())
            })
        })

        setDataToDisplay(filtered)
    }, [data, filters])

    const uploadSuccess = (rows, notice) => {
        setData(rows)
        setNotice(notice)
        setLoading(false)
    }

    const columns = [
        {
            field: '_status',
            headerName: '',
            width: 44,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => {
                const missing = getMissingFields(params.row)
                if (missing.length === 0) return null
                return (
                    <Tooltip title={`Missing: ${missing.join(', ')}`}>
                        <Warning color='warning' fontSize='small' />
                    </Tooltip>
                )
            },
        },
        {
            field: 'varcond',
            headerName: 'Varcond',
            flex: 1,
            minWidth: 120,
            renderHeader: () => (
                <Header
                    title='Varcond'
                    onFilter={handleFilterChange}
                    field='varcond'
                    filterValue={filters.varcond}
                    id='filter-qm-varcond'
                />
            ),
        },
        {
            field: 'productType',
            headerName: 'Product Type',
            flex: 1,
            minWidth: 150,
            renderHeader: () => (
                <Header
                    title='Product Type'
                    onFilter={handleFilterChange}
                    field='productType'
                    filterValue={filters.productType}
                    id='filter-qm-producttype'
                />
            ),
        },
        {
            field: 'size',
            headerName: 'Size',
            flex: 1,
            minWidth: 120,
            renderHeader: () => (
                <Header
                    title='Size'
                    onFilter={handleFilterChange}
                    field='size'
                    filterValue={filters.size}
                    id='filter-qm-size'
                />
            ),
        },
        {
            field: 'assembly',
            headerName: 'Assembly',
            flex: 1,
            minWidth: 130,
            renderHeader: () => (
                <Header
                    title='Assembly'
                    onFilter={handleFilterChange}
                    field='assembly'
                    filterValue={filters.assembly}
                    id='filter-qm-assembly'
                />
            ),
        },
        {
            field: 'varcode',
            headerName: 'Varcode',
            flex: 1,
            minWidth: 120,
            renderHeader: () => (
                <Header
                    title='Varcode'
                    onFilter={handleFilterChange}
                    field='varcode'
                    filterValue={filters.varcode}
                    id='filter-qm-varcode'
                />
            ),
        },
        {
            field: 'price',
            headerName: 'Price',
            flex: 1,
            minWidth: 130,
            renderCell: (params) => {
                if (params.value === undefined || params.value === null) {
                    return (
                        <span style={{ color: 'red', background: 'white' }}>
                            <Warning color='error' className='warningIcon' /> No Price
                        </span>
                    )
                }
                return params.value
            },
            renderHeader: () => (
                <Header
                    title='Price'
                    onFilter={handleFilterChange}
                    field='price'
                    filterValue={filters.price}
                    id='filter-qm-price'
                />
            ),
        },
    ]

    const deletion = useGridRowDeletion({
        enabled: isProjectEditor,
        rows: dataToDisplay,
        getRowId,
        url: `${apiUrl}/rooftop/qm/`,
        tableLabel: 'QM prices',
        filtered: hasActiveFilters(undefined, filters),
        onDeleted: (ids) => setData((previous) => previous.filter((row) => !ids.has(row.id))),
    })

    return (
        <div className='page-container'>
            <AppPageHeader
                title='QM prices'
                actionsLabel='QM prices actions'
                notice={notice}
                onCloseNotice={() => setNotice(null)}
                actions={
                    isProjectEditor && (
                        <RooftopPriceActions
                            tableLabel='QM prices'
                            api='/rooftop/qm/'
                            exportId='download-prices-qm'
                            data={data}
                            columns={columns}
                            fileName='QM_Prices.xlsx'
                            sheetName='QM_Prices'
                            onImportStart={() => {
                                setNotice(null)
                                setLoading(true)
                            }}
                            onImportSuccess={uploadSuccess}
                            onImportError={(message) => {
                                setLoading(false)
                                setNotice({ severity: 'error', message: `Import failed: ${message || 'unknown error'}` })
                            }}
                            onSyncComplete={refreshData}
                            onSyncError={(message) => setNotice(message ? { severity: 'error', message } : null)}
                        />
                    )
                }
            />

            <div className='table-container'>
                <AppDataGrid
                    autoHeight={false}
                    rows={dataToDisplay}
                    columns={deletion.withActionsColumn(columns)}
                    getRowId={getRowId}
                    loading={loading}
                    pageSizeOptions={[5, 10, 20]}
                    initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                    rowHeight={70}
                    {...deletion.gridProps}
                />
                {deletion.dialog}
            </div>
        </div>
    )
}

export default QmPage
